// src/components/receipt/receiptShared.jsx
import React from "react";
import { Field, ErrorMessage, useFormikContext } from "formik";
import { FaRupeeSign } from "react-icons/fa";
import Select from "react-select";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { generateReceipt } from "../../services/receipt.service";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

// A cleared <input type="number"> gives "" in Formik; "" + 50 would become "50" (string).
export const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

export const getMinDate = () => {
    const d = new Date();
    return new Date(d.getFullYear() - 20, d.getMonth(), d.getDate());
};

export const getMaxDate = () => {
    const d = new Date();
    return new Date(d.getFullYear() + 50, d.getMonth(), d.getDate());
};

export const hasPaymentDetails = (paymentDetails) =>
    !!paymentDetails && Object.keys(paymentDetails).length > 0;

export const PAYMENT_TYPES = [
    { label: "Cash", value: "CA" },
    { label: "Cheque", value: "CQ" },
    { label: "RTGS", value: "RT" },
    { label: "Online", value: "Online" },
];

// Fetch a master list and store it; shows one consistent error message.
export const loadMasterList = async (fetcher, setter, label) => {
    try {
        const response = await fetcher();
        if (response) {
            setter(response);
        } else {
            setter([]);
            Swal.fire("Error", `Failed to fetch ${label} list`, "error");
        }
    } catch (error) {
        Swal.fire("Error", `Something went wrong while fetching ${label} list`, "error");
        console.error(error);
    }
};

// Maps the Razorpay payment object to the DTO your backend expects.
export const buildPaymentDto = (paymentData) => {
    if (!paymentData) return {};
    const dto = {
        razorPayId: paymentData.id || paymentData.paymentId || paymentData.razorpay_payment_id || "",
        orderId: paymentData.orderId || paymentData.razorpay_order_id || "",
        signature: paymentData.signature || paymentData.razorpay_signature || "",
        method: paymentData.method || "online",
    };

    if (paymentData.method === "netbanking") {
        dto.bankName = paymentData.bank;
    } else if (paymentData.method === "upi") {
        dto.upiNo = paymentData.vpa || paymentData.upi?.vpa;
    } else if (paymentData.method === "card") {
        dto.cardNo = paymentData.card?.last4 ? String(paymentData.card.last4) : "";
        dto.cardType = paymentData.card?.type;
    } else if (paymentData.method === "wallet") {
        dto.wallet = paymentData.wallet;
    }
    return dto;
};

// Money has already been taken when this runs, so never give up silently:
// retry, then tell the user the payment id so the office can fix it manually.
export const generateReceiptWithRetry = async (receiptData, paymentId, maxAttempts = 3) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            const response = await generateReceipt(receiptData);
            if (response) return response;
        } catch (error) {
            console.error(`Receipt generation failed (attempt ${attempt})`, error);
        }
        if (attempt < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, 1500 * attempt));
        }
    }

    await Swal.fire({
        icon: "error",
        title: "Payment received, receipt not generated",
        html:
            `Your payment <b>${paymentId}</b> was successful, but receipt creation timed out.<br/>` +
            "Please contact the temple office with this payment ID. <b>Do not pay again.</b>",
    });
    return null;
};

// Yup rules common to all three forms.
export const commonDevoteeSchema = {
    devoteeName: Yup.string().required("Devotee name is required"),
    mobile: Yup.string()
        .required("Mobile number is required")
        .matches(/^\d{10}$/, "Must be a 10 digit number"),
    alternateMobile: Yup.string()
        .notRequired()
        .matches(/^\d{10}$/, { message: "Must be a 10 digit number", excludeEmptyString: true }),
    nakshatra: Yup.string().required("Nakshatra is required"),
    gotra: Yup.string().required("Gotra is required"),
    paymentType: Yup.string().required("Payment type is required"),
};

export const getOthersGotraId = (gotraList = []) => {
    const item = gotraList.find((g) => g.gotraName && g.gotraName.trim().toLowerCase() === "others");
    return item ? String(item.gotraId) : "0";
};

export const getOthersNakshatraId = (nakshatraList = []) => {
    const item = nakshatraList.find((n) => n.nakshatraName && n.nakshatraName.trim().toLowerCase() === "others");
    return item ? String(item.nakshatraId) : "0";
};

export const getDefaultGotra = (devoteeData, gotraList = []) => {
    if (devoteeData?.gotra !== undefined && devoteeData?.gotra !== null && String(devoteeData.gotra).trim() !== "") {
        return String(devoteeData.gotra);
    }
    return getOthersGotraId(gotraList);
};

export const getDefaultNakshatra = (devoteeData, nakshatraList = []) => {
    if (devoteeData?.nakshatra !== undefined && devoteeData?.nakshatra !== null && String(devoteeData.nakshatra).trim() !== "") {
        return String(devoteeData.nakshatra);
    }
    return getOthersNakshatraId(nakshatraList);
};

/* ------------------------------------------------------------------ */
/* UI pieces                                                           */
/* ------------------------------------------------------------------ */

export const MobileEntryCard = ({ mobileNumber, setMobileNumber, onSubmit }) => (
    <div className="mobile-entry">
        <h5>Enter Mobile Number</h5>
        <div className="input-group">
            <input
                type="text"
                inputMode="numeric"
                className="form-control"
                placeholder="Enter Mobile Number"
                value={mobileNumber}
                maxLength={10}
                onChange={(e) => {
                    const input = e.target.value;
                    if (/^\d*$/.test(input)) {
                        setMobileNumber(input);
                    }
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter") onSubmit();
                }}
            />
            <button type="button" className="btn btn-primary" onClick={onSubmit}>
                Generate Form
            </button>
        </div>
    </div>
);

export const PaymentLoader = ({ text = "Verifying Payment..." }) => (
    <div className="loader-overlay">
        <div className="loader"></div>
        <p className="text-white fw-bold mt-2">{text}</p>
    </div>
);

export const DevoteeDetailsSection = ({
    values,
    setFieldValue,
    nakshatraOptions,
    gotraOptions,
    isNameDisabled = false,
    showCancel = false,
    onCancel,
}) => (
    <>
        <div className="mb-3">
            <h4 className="section-title mb-0">Devotee Details</h4>
        </div>
        <div className="row g-3 text-start">
            <div className="col-md-4">
                <label className="form-label">Devotee Name</label>
                <Field
                    name="devoteeName"
                    className="form-control"
                    readOnly={isNameDisabled}
                    placeholder={isNameDisabled ? "" : "Enter Devotee Name"}
                />
                <ErrorMessage name="devoteeName" component="div" className="text-danger" />
            </div>
            <div className="col-md-2">
                <label className="form-label">Mobile Number</label>
                <Field name="mobile" className="form-control" readOnly />
                <ErrorMessage name="mobile" component="div" className="text-danger" />
            </div>
            <div className="col-md-2">
                <label className="form-label">Alternate Mobile</label>
                <Field
                    name="alternateMobile"
                    className="form-control"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="Alternate Mobile"
                    onChange={(e) => {
                        const input = e.target.value;
                        if (/^\d*$/.test(input)) {
                            setFieldValue("alternateMobile", input);
                        }
                    }}
                />
                <ErrorMessage name="alternateMobile" component="div" className="text-danger" />
            </div>
            <div className="col-md-2">
                <label className="form-label">Nakshatra</label>
                <Select
                    className="text-start"
                    classNamePrefix="rs"
                    options={nakshatraOptions}
                    value={nakshatraOptions.find((opt) => String(opt.value) === String(values.nakshatra)) || null}
                    onChange={(selected) => setFieldValue("nakshatra", selected ? String(selected.value) : "")}
                    isClearable
                    placeholder="Select"
                />
                <ErrorMessage name="nakshatra" component="div" className="text-danger" />
            </div>
            <div className="col-md-2">
                <label className="form-label">Gotra</label>
                <Select
                    className="text-start"
                    classNamePrefix="rs"
                    options={gotraOptions}
                    value={gotraOptions.find((opt) => String(opt.value) === String(values.gotra)) || null}
                    onChange={(selected) => setFieldValue("gotra", selected ? String(selected.value) : "")}
                    isClearable
                    placeholder="Select"
                />
                <ErrorMessage name="gotra" component="div" className="text-danger" />
            </div>
        </div>
    </>
);

export const PaymentDetailsPreview = ({ paymentDetails }) => {
    if (!hasPaymentDetails(paymentDetails)) return null;

    const Item = ({ label, children, cols }) => (
        <div className={`col-sm-${cols} mb-2`}>
            <div className="text-muted text-uppercase fw-semibold small">{label}</div>
            <div>{children}</div>
        </div>
    );

    return (
        <div className="payment-preview">
            <h6>Payment Details Preview</h6>
            {paymentDetails.chequeNo ? (
                <div className="row">
                    <Item label="Cheque No" cols={4}>{paymentDetails.chequeNo}</Item>
                    <Item label="Cheque Date" cols={4}>
                        {paymentDetails.chequeDate
                            ? new Date(paymentDetails.chequeDate).toLocaleDateString("en-GB").replace(/\//g, "-")
                            : ""}
                    </Item>
                    <Item label="Bank Name" cols={4}>{paymentDetails.bankName}</Item>
                </div>
            ) : paymentDetails.rtgsNo ? (
                <div className="row">
                    <Item label="RTGS No" cols={6}>{paymentDetails.rtgsNo}</Item>
                    <Item label="Bank Name" cols={6}>{paymentDetails.bankName}</Item>
                </div>
            ) : null}
        </div>
    );
};

export const GrandTotal = ({ amount }) => (
    <>
        <label className="form-label fw-bold">Grand Total</label>
        <div className="grand-total">
            <FaRupeeSign /> {amount}
        </div>
    </>
);

export const PaymentTypeSelect = ({ onChange, onEditDetails }) => {
    const { values, setFieldTouched } = useFormikContext();
    const needsDetails = ["CQ", "RT"].includes(values.paymentType);

    return (
        <>
            <label className="form-label fw-bold" htmlFor="paymentType">Payment Type</label>
            <Select
                inputId="paymentType"
                className="text-start"
                classNamePrefix="rs"
                options={PAYMENT_TYPES}
                value={PAYMENT_TYPES.find((p) => p.value === values.paymentType) || null}
                onChange={(option) => onChange({ target: { value: option ? option.value : "" } })}
                onBlur={() => setFieldTouched("paymentType", true)}
                isClearable
                isSearchable={false}
                placeholder="Select"
            />
            <ErrorMessage name="paymentType" component="div" className="text-danger" />

            {needsDetails && onEditDetails && (
                <button type="button" className="btn btn-link btn-sm p-0 mt-1" onClick={onEditDetails}>
                    {hasPaymentDetails(values.paymentDetails) ? "Edit details" : "Enter details"}
                </button>
            )}
        </>
    );
};

export const SubmitBar = ({ paymentType, loading, onCancel, showCancel = false }) => (
    <div className="submit-bar d-flex justify-content-center align-items-center gap-3 flex-wrap">
        {showCancel && (
            <button
                type="button"
                className="btn btn-secondary btn-lg px-5 text-uppercase"
                disabled={loading}
                onClick={onCancel}
            >
                Cancel
            </button>
        )}
        <button
            type="submit"
            className="btn btn-success btn-lg px-5 text-uppercase"
            disabled={loading}
        >
            {paymentType === "Online" ? "Pay & Submit Receipt" : "Submit Receipt"}
        </button>
    </div>
);
